import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '@/context/AuthContext';
import '@/styles/TemplateRenderer.scss';
import { FaStar, FaCheck, FaPen, FaSignature, FaTimes, FaSpinner } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as Sentry from '@sentry/react';

/**
 * Dynamic form renderer that generates UI from Action Item template JSON schemas
 * Supports multiple field types, validation, and layout sections
 */
const TemplateRenderer = ({
    template,
    initialData = {},
    onSubmit,
    onCancel,
    onChange,
    submitButtonText = 'Submit',
    readOnly = false,
    externalPropertyId = null
}) => {
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const { apiFetch, user } = useAuth();
    const [userSignature, setUserSignature] = useState(null);
    const [signatureLoading, setSignatureLoading] = useState(false);

    // Data-linked field state: property selector, tenant search, shop/unit list
    const [dlProperties, setDlProperties] = useState([]);
    const [dlPropertiesLoading, setDlPropertiesLoading] = useState(false);
    const [dlTenantResults, setDlTenantResults] = useState({});         // keyed by field name
    const [dlTenantLoading, setDlTenantLoading] = useState({});
    const [dlTenantDropdownOpen, setDlTenantDropdownOpen] = useState({});
    const [dlShopResults, setDlShopResults] = useState({});             // keyed by field name
    const [dlShopLoading, setDlShopLoading] = useState({});
    const tenantSearchTimers = useRef({});

    // Shop number search state (for searchable shop-number fields)
    const [dlShopSearchText, setDlShopSearchText] = useState({});
    const [dlShopDropdownOpen, setDlShopDropdownOpen] = useState({});

    // Entra user selector state
    const [entraSearchResults, setEntraSearchResults] = useState({});    // keyed by field name
    const [entraSearchLoading, setEntraSearchLoading] = useState({});
    const [entraDropdownOpen, setEntraDropdownOpen] = useState({});
    const entraSearchTimers = useRef({});

    // Role selector state
    const [capTracRoles, setCapTracRoles] = useState([]);
    const [capTracRolesLoading, setCapTracRolesLoading] = useState(false);

    // Lookup selector state
    const [lookupResults, setLookupResults] = useState({});        // keyed by field name
    const [lookupLoadingMap, setLookupLoadingMap] = useState({});
    const lookupTimers = useRef({});

    // Parse template schema
    const schema = typeof template.schema === 'string' 
        ? JSON.parse(template.schema) 
        : template.schema;

    // Support both old (flat fields) and new (sections-based) structures.
    // Memoized to prevent the allFields useMemo below from recomputing on every render.
    const fields = useMemo(() => schema.fields || [], [schema.fields]);
    const templateSections = useMemo(() => schema.sections || [], [schema.sections]);
    
    // Extract all fields from sections (including those in rows).
    // Wrapped in useMemo to avoid recreating the array on every render, which would
    // invalidate downstream hooks that depend on allFields.
    const allFields = useMemo(() => {
        const result = [];
        if (templateSections.length > 0) {
            templateSections.forEach(section => {
                section.fields?.forEach(field => {
                    if (field.type === 'row' && field.columns) {
                        // Extract fields from row columns
                        field.columns.forEach(column => {
                            column.fields?.forEach(colField => result.push(colField));
                        });
                    } else {
                        result.push(field);
                    }
                });
            });
        } else {
            // Old structure - use fields directly, extracting from rows if needed
            fields.forEach(field => {
                if (field.type === 'row' && field.columns && Array.isArray(field.columns)) {
                    // Extract fields from row columns with defensive checks
                    field.columns.forEach(column => {
                        if (column.fields && Array.isArray(column.fields)) {
                            column.fields.forEach(colField => result.push(colField));
                        }
                    });
                } else {
                    result.push(field);
                }
            });
        }
        return result;
    }, [templateSections, fields]);
    
    // Create a field lookup map for O(1) performance
    const fieldMap = new Map(allFields.map(f => [f.name, f]));

    /**
     * Resolve a default value expression to its actual value.
     * Supports: static strings, {{today}}, {{now}}, {{currentUser}}, {{currentUserEmail}},
     * and template interpolation like "REF-{{today}}-{{propertyName}}".
     */
    const resolveDefaultExpression = (expression) => {
        if (!expression || typeof expression !== 'string') return expression;
        const today = new Date().toISOString().split('T')[0];
        const now = new Date().toISOString().slice(0, 16);
        const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.displayName || '';
        const userEmail = user?.email || user?.Email || '';
        return expression
            .replace(/\{\{today\}\}/gi, today)
            .replace(/\{\{now\}\}/gi, now)
            .replace(/\{\{currentUser\}\}/gi, userName)
            .replace(/\{\{currentUserEmail\}\}/gi, userEmail);
    };

    // Initialize default values from field definitions on mount
    useEffect(() => {
        async function run() {
            const defaults = {};
            let hasDefaults = false;
            allFields.forEach(f => {
                // Use explicit null/undefined/empty-string check instead of falsy (!value) so that
                // legitimate values like 0 (number) or false (checkbox) are never overwritten by defaults.
                const currentValue = formData[f.name];
                const fieldIsEmpty = currentValue === undefined || currentValue === null || currentValue === '';
                if (f.defaultValue !== undefined && f.defaultValue !== '' && fieldIsEmpty) {
                    const resolved = resolveDefaultExpression(f.defaultValue);
                    if (resolved !== undefined && resolved !== '') {
                        defaults[f.name] = resolved;
                        hasDefaults = true;
                    }
                }
            });
            if (hasDefaults) {
                setFormData(prev => {
                    const next = { ...prev };
                    Object.entries(defaults).forEach(([key, val]) => {
                        // Same null/undefined/empty-string guard inside the updater so 0 and false
                        // values already in state are not replaced by the resolved default.
                        const existingValue = next[key];
                        const isEmpty = existingValue === undefined || existingValue === null || existingValue === '';
                        if (isEmpty) next[key] = val;
                    });
                    if (onChange) onChange(next);
                    return next;
                });
            }
        }
        run();
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch the user's saved signature if any signature field exists in the template
    const hasSignatureField = allFields.some(f => f.type === 'signature');
    useEffect(() => {
        if (!hasSignatureField || readOnly) return;
        let cancelled = false;
        const loadSignature = async () => {
            setSignatureLoading(true);
            try {
                const res = await apiFetch('/api/User/signature');
                const data = await res.json();
                if (!cancelled && data.success && data.data?.signatureData) {
                    setUserSignature(data.data.signatureData);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // No signature saved — user will be prompted to add one
            } finally {
                if (!cancelled) setSignatureLoading(false);
            }
        };
        loadSignature();
        return () => { cancelled = true; };
    }, [hasSignatureField, readOnly, apiFetch]);

    // Load user-accessible properties when a property-selector field exists.
    // Security: fetches from the permission-filtered endpoint first, only falling back
    // to /api/Property/all when the user has unrestricted access (per their permissions payload).
    const hasPropertySelector = allFields.some(f => f.type === 'property-selector');
    useEffect(() => {
        if (!hasPropertySelector) return;
        let cancelled = false;

        const normalizeAndSortProperties = (properties) => {
            if (!Array.isArray(properties)) return [];
            return [...properties]
                .filter(property => property?.isActive !== false && property?.active !== false)
                .sort((a, b) => (a?.propertyName || '').localeCompare(b?.propertyName || ''));
        };

        const loadProperties = async () => {
            setDlPropertiesLoading(true);
            try {
                // Resolve the authenticated user's ID from the auth context
                const currentUserId = user?.userID || user?.UserID || user?.id;
                if (!currentUserId) {
                    if (!cancelled) setDlProperties([]);
                    return;
                }

                // First check user's property access permissions
                const permissionsResponse = await apiFetch(
                    `/api/Permissions/my-accessible-properties/${encodeURIComponent(currentUserId)}`
                );
                const permissionsData = await permissionsResponse.json();

                if (!permissionsData?.success) {
                    if (!cancelled) setDlProperties([]);
                    return;
                }

                let propertiesToDisplay = [];

                if (permissionsData?.data?.unrestricted) {
                    // User has unrestricted access — load all properties
                    const allPropertiesResponse = await apiFetch('/api/Property/all');
                    const allPropertiesData = await allPropertiesResponse.json();
                    if (allPropertiesData?.success && Array.isArray(allPropertiesData?.data)) {
                        propertiesToDisplay = allPropertiesData.data;
                    }
                } else if (Array.isArray(permissionsData?.data?.properties)) {
                    // User has restricted access — use only their permitted properties
                    propertiesToDisplay = permissionsData.data.properties;
                } else if (Array.isArray(permissionsData?.data)) {
                    // Alternate payload shape: property list returned directly as data array
                    propertiesToDisplay = permissionsData.data;
                }

                if (!cancelled) {
                    setDlProperties(normalizeAndSortProperties(propertiesToDisplay));
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                if (!cancelled) setDlProperties([]);
            } finally {
                if (!cancelled) setDlPropertiesLoading(false);
            }
        };

        loadProperties();
        return () => { cancelled = true; };
    }, [hasPropertySelector, apiFetch, user]);

    /**
     * Search tenants for a specific property (used by tenant-selector fields).
     * Debounced: clears previous timer for the same field name.
     */
    const searchTenantsForField = useCallback((fieldName, propertyId, searchText) => {
        if (tenantSearchTimers.current[fieldName]) clearTimeout(tenantSearchTimers.current[fieldName]);
        const minLen = 2;
        if (!propertyId || !searchText || searchText.length < minLen) {
            setDlTenantResults(prev => ({ ...prev, [fieldName]: [] }));
            return;
        }
        tenantSearchTimers.current[fieldName] = setTimeout(async () => {
            setDlTenantLoading(prev => ({ ...prev, [fieldName]: true }));
            try {
                const res = await apiFetch(`/api/AssetManagement/mda/tenants?propertyId=${propertyId}&search=${encodeURIComponent(searchText)}`);
                const data = await res.json();
                if (data.success) {
                    setDlTenantResults(prev => ({ ...prev, [fieldName]: data.data || [] }));
                    setDlTenantDropdownOpen(prev => ({ ...prev, [fieldName]: true }));
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setDlTenantResults(prev => ({ ...prev, [fieldName]: [] }));
            } finally {
                setDlTenantLoading(prev => ({ ...prev, [fieldName]: false }));
            }
        }, 300);
    }, [apiFetch]);

    /**
     * Search Entra ID users (used by entra-user-selector fields).
     * Debounced: clears previous timer for the same field name.
     */
    const searchEntraUsersForField = useCallback((fieldName, searchText) => {
        if (entraSearchTimers.current[fieldName]) clearTimeout(entraSearchTimers.current[fieldName]);
        if (!searchText || searchText.length < 2) {
            setEntraSearchResults(prev => ({ ...prev, [fieldName]: [] }));
            return;
        }
        entraSearchTimers.current[fieldName] = setTimeout(async () => {
            setEntraSearchLoading(prev => ({ ...prev, [fieldName]: true }));
            try {
                const res = await apiFetch(`/api/Entra/users/search?q=${encodeURIComponent(searchText)}&top=8`);
                const data = await res.json();
                if (data.success) {
                    setEntraSearchResults(prev => ({ ...prev, [fieldName]: data.data || [] }));
                    setEntraDropdownOpen(prev => ({ ...prev, [fieldName]: true }));
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setEntraSearchResults(prev => ({ ...prev, [fieldName]: [] }));
            } finally {
                setEntraSearchLoading(prev => ({ ...prev, [fieldName]: false }));
            }
        }, 350);
    }, [apiFetch]);

    /**
     * Validate a single field
     */
    const validateField = (field, value) => {
        const fieldDef = fieldMap.get(field);
        if (!fieldDef) return null;

        // Required validation
        if (fieldDef.required && !value) {
            return `${fieldDef.label} is required`;
        }

        // Type-specific validation
        if (value) {
            switch (fieldDef.type) {
                case 'email': {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) {
                        return 'Please enter a valid email address';
                    }
                    break;
                }

                case 'number':
                    if (isNaN(value)) {
                        return 'Please enter a valid number';
                    }
                    if (fieldDef.validation) {
                        if (fieldDef.validation.min !== undefined && Number(value) < fieldDef.validation.min) {
                            return `Value must be at least ${fieldDef.validation.min}`;
                        }
                        if (fieldDef.validation.max !== undefined && Number(value) > fieldDef.validation.max) {
                            return `Value must be at most ${fieldDef.validation.max}`;
                        }
                    }
                    break;

                case 'text':
                case 'textarea':
                    if (fieldDef.validation) {
                        if (fieldDef.validation.minLength && value.length < fieldDef.validation.minLength) {
                            return `Must be at least ${fieldDef.validation.minLength} characters`;
                        }
                        if (fieldDef.validation.maxLength && value.length > fieldDef.validation.maxLength) {
                            return `Must be at most ${fieldDef.validation.maxLength} characters`;
                        }
                        if (fieldDef.validation.pattern) {
                            const regex = new RegExp(fieldDef.validation.pattern);
                            if (!regex.test(value)) {
                                return fieldDef.validation.patternMessage || 'Invalid format';
                            }
                        }
                    }
                    break;
            }
        }

        return null;
    };

    /**
     * Handle field value change
     */
    const handleChange = (fieldName, value) => {
        setFormData(prev => {
            const next = { ...prev, [fieldName]: value };
            // Notify parent of data change if callback provided
            if (onChange) onChange(next);
            return next;
        });

        // Validate on change if field has been touched
        if (touched[fieldName]) {
            const error = validateField(fieldName, value);
            setErrors(prev => ({
                ...prev,
                [fieldName]: error
            }));
        }
    };

    /**
     * When an Entra user is selected, fetch their full details and auto-populate linked fields.
     */
    const handleEntraUserSelected = useCallback(async (fieldDef, entraUser) => {
        // Set the display name on the selector field itself
        handleChange(fieldDef.name, entraUser.displayName || '');
        setEntraDropdownOpen(prev => ({ ...prev, [fieldDef.name]: false }));

        // Store the Entra user ID for workflow automation reference
        handleChange(`${fieldDef.name}_entraId`, entraUser.id || '');

        // Fetch full details (including manager and device)
        try {
            const res = await apiFetch(`/api/Entra/users/${entraUser.id}/details`);
            const data = await res.json();
            if (data.success && data.data) {
                const details = data.data;
                const mappings = fieldDef.autoPopulate || {};
                // Auto-populate linked fields from the mapping config
                Object.entries(mappings).forEach(([sourceKey, targetFieldName]) => {
                    if (details[sourceKey] !== undefined && details[sourceKey] !== null) {
                        handleChange(targetFieldName, String(details[sourceKey]));
                    }
                });
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // Non-critical — the user was already selected, just couldn't auto-fill
        }
    }, [apiFetch, handleChange]);

    // Load CapTrac roles when a role-selector field exists
    const hasRoleSelector = allFields.some(f => f.type === 'role-selector');
    useEffect(() => {
        if (!hasRoleSelector || readOnly) return;
        let cancelled = false;
        const loadRoles = async () => {
            setCapTracRolesLoading(true);
            try {
                const res = await apiFetch('/api/Entra/roles');
                const data = await res.json();
                if (!cancelled && data.success) {
                    setCapTracRoles(data.data || []);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
            } finally {
                if (!cancelled) setCapTracRolesLoading(false);
            }
        };
        loadRoles();
        return () => { cancelled = true; };
    }, [hasRoleSelector, readOnly, apiFetch]);

    /**
     * Load shop/unit numbers for a specific property (used by shop-number fields).
     */
    const loadShopsForField = useCallback(async (fieldName, propertyId) => {
        if (!propertyId) {
            setDlShopResults(prev => ({ ...prev, [fieldName]: [] }));
            return;
        }
        setDlShopLoading(prev => ({ ...prev, [fieldName]: true }));
        try {
            const res = await apiFetch(`/api/AssetManagement/mda/rentable-units?propertyId=${propertyId}`);
            const data = await res.json();
            if (data.success) {
                setDlShopResults(prev => ({ ...prev, [fieldName]: data.data || [] }));
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setDlShopResults(prev => ({ ...prev, [fieldName]: [] }));
        } finally {
            setDlShopLoading(prev => ({ ...prev, [fieldName]: false }));
        }
    }, [apiFetch]);

    // Automatically load shops when the linked property changes for shop-number fields.
    // This replaces the previous render-path invocation (which could cause repeated fetches
    // and render loops under React StrictMode) with a proper side-effect.
    const shopNumberFields = useMemo(
        () => allFields.filter(f => f.type === 'shop-number' && f.dependsOnField),
        [allFields]
    );
    const shopPropertyDependencies = useMemo(() => {
        return shopNumberFields.map(f => {
            // For the virtual __actionItemProperty__ sentinel or no dependsOnField, use externalPropertyId
            if (f.dependsOnField === '__actionItemProperty__' || !f.dependsOnField) {
                return { fieldName: f.name, propertyId: externalPropertyId ? parseInt(externalPropertyId, 10) || null : null };
            }
            const linkedValue = formData[f.dependsOnField];
            return { fieldName: f.name, propertyId: linkedValue ? parseInt(linkedValue, 10) || null : null };
        });
    }, [shopNumberFields, formData, externalPropertyId]);

    useEffect(() => {
        shopPropertyDependencies.forEach(({ fieldName, propertyId }) => {
            if (propertyId) {
                loadShopsForField(fieldName, propertyId);
            } else {
                setDlShopResults(prev => ({ ...prev, [fieldName]: [] }));
            }
        });
    }, [shopPropertyDependencies, loadShopsForField]);

    // Sync external property ID (from CreateActionItemModal top-level property) into
    // property-selector fields that have syncWithActionItemProperty enabled.
    useEffect(() => {
        if (!externalPropertyId || !hasPropertySelector) return;
        const propId = String(externalPropertyId);
        allFields.forEach(f => {
            if (f.type === 'property-selector' && f.syncWithActionItemProperty !== false) {
                setFormData(prev => {
                    if (prev[f.name] === propId) return prev;
                    const next = { ...prev, [f.name]: propId };
                    // Clear dependent fields
                    allFields.forEach(dep => {
                        if ((dep.type === 'tenant-selector' || dep.type === 'shop-number') && dep.dependsOnField === f.name) {
                            next[dep.name] = '';
                        }
                    });
                    if (onChange) onChange(next);
                    return next;
                });
                // Load shops for dependent shop-number fields
                allFields.forEach(dep => {
                    if (dep.type === 'shop-number' && dep.dependsOnField === f.name) {
                        loadShopsForField(dep.name, parseInt(propId, 10));
                    }
                });
            }
        });
    }, [externalPropertyId, hasPropertySelector, dlProperties, allFields, loadShopsForField, onChange]);

    // When externalPropertyId changes, update tenant-selector / shop-number fields
    // that use the external property (via sentinel or no explicit dependsOnField)
    useEffect(() => {
        async function run() {
            if (!externalPropertyId) return;
            const propId = parseInt(externalPropertyId, 10);
            if (!propId) return;

            // Clear dependent fields when property changes
            await Promise.resolve();
            setFormData(prev => {
                let changed = false;
                const next = { ...prev };
                allFields.forEach(f => {
                    const usesExternal = f.dependsOnField === '__actionItemProperty__' || !f.dependsOnField;
                    if (usesExternal && (f.type === 'tenant-selector' || f.type === 'shop-number')) {
                        if (next[f.name]) {
                            next[f.name] = '';
                            changed = true;
                        }
                    }
                });
                if (changed && onChange) onChange(next);
                return changed ? next : prev;
            });

            // Pre-load shops for shop-number fields using external property
            allFields.forEach(f => {
                const usesExternal = f.dependsOnField === '__actionItemProperty__' || !f.dependsOnField;
                if (f.type === 'shop-number' && usesExternal) {
                    loadShopsForField(f.name, propId);
                }
            });
        }
        run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalPropertyId]);

    /**
     * Resolve the property ID for a field that depends on a property selector.
     * Priority:
     *  1. '__actionItemProperty__' sentinel → use externalPropertyId prop
     *  2. Named dependsOnField → look up in formData
     *  3. No dependsOnField but externalPropertyId available → fall back to externalPropertyId
     *     (so tenant-selector / shop-number fields work without explicit config)
     */
    const resolveLinkedPropertyId = (fieldDef) => {
        const linkedFieldName = fieldDef.dependsOnField;
        // Virtual sentinel — piggyback off the Action Item's property selector
        if (linkedFieldName === '__actionItemProperty__') {
            return externalPropertyId ? parseInt(externalPropertyId, 10) || null : null;
        }
        // Explicit link to a property-selector field in the template
        if (linkedFieldName) {
            const val = formData[linkedFieldName];
            return val ? parseInt(val, 10) || null : null;
        }
        // Fallback: if externalPropertyId is available, use it automatically
        if (externalPropertyId) {
            return parseInt(externalPropertyId, 10) || null;
        }
        return null;
    };

    /**
     * Evaluate a single condition against current form data.
     * Returns true if the condition is met.
     */
    const evaluateCondition = (condition) => {
        if (!condition?.fieldId) return true;
        // Find the target field by id or name
        const targetField = allFields.find(f => f.id === condition.fieldId || f.name === condition.fieldId);
        if (!targetField) return true;
        const fieldValue = formData[targetField.name];
        const conditionValue = condition.value;

        switch (condition.operator) {
            case 'equals':
                return String(fieldValue || '') === String(conditionValue || '');
            case 'notEquals':
                return String(fieldValue || '') !== String(conditionValue || '');
            case 'contains':
                return String(fieldValue || '').toLowerCase().includes(String(conditionValue || '').toLowerCase());
            case 'notContains':
                return !String(fieldValue || '').toLowerCase().includes(String(conditionValue || '').toLowerCase());
            case 'isEmpty':
                return !fieldValue || String(fieldValue).trim() === '';
            case 'isNotEmpty':
                return !!fieldValue && String(fieldValue).trim() !== '';
            case 'greaterThan':
                return Number(fieldValue) > Number(conditionValue);
            case 'lessThan':
                return Number(fieldValue) < Number(conditionValue);
            case 'isChecked':
                return fieldValue === true || fieldValue === 'true' || fieldValue === '1' || fieldValue === 'yes';
            case 'isNotChecked':
                return !fieldValue || fieldValue === false || fieldValue === 'false' || fieldValue === '0' || fieldValue === 'no';
            default:
                return true;
        }
    };

    /**
     * Determine whether a field should be visible based on its conditional logic.
     * Fields without conditions are always visible.
     * conditionLogic: 'all' = AND (all must pass), 'any' = OR (at least one must pass).
     */
    const isFieldVisible = (fieldDef) => {
        const conditions = fieldDef.conditions;
        if (!conditions || conditions.length === 0) return true;
        const logic = fieldDef.conditionLogic || 'all';
        if (logic === 'any') {
            return conditions.some(evaluateCondition);
        }
        return conditions.every(evaluateCondition);
    };

    /**
     * Fetch data for a lookup-selector field from its configured API endpoint.
     * Supports debounced search and dependency on other fields.
     */
    const fetchLookupData = (fieldDef, searchText) => {
        const fieldName = fieldDef.name;
        if (lookupTimers.current[fieldName]) clearTimeout(lookupTimers.current[fieldName]);

        lookupTimers.current[fieldName] = setTimeout(async () => {
            if (!fieldDef.lookupEndpoint) return;
            setLookupLoadingMap(prev => ({ ...prev, [fieldName]: true }));
            try {
                let url = fieldDef.lookupEndpoint;
                const params = new URLSearchParams();
                if (searchText) params.set('search', searchText);
                if (fieldDef.lookupDependsOn && fieldDef.lookupQueryParam) {
                    const depValue = formData[fieldDef.lookupDependsOn];
                    if (depValue) params.set(fieldDef.lookupQueryParam, depValue);
                }
                const qs = params.toString();
                if (qs) url += (url.includes('?') ? '&' : '?') + qs;

                const response = await apiFetch(url);
                if (response.ok) {
                    const data = await response.json();
                    setLookupResults(prev => ({ ...prev, [fieldName]: Array.isArray(data) ? data : [] }));
                }
            } catch (err) {
                Sentry.captureException(err);
            } finally {
                setLookupLoadingMap(prev => ({ ...prev, [fieldName]: false }));
            }
        }, 300);
    };

    /**
     * Load initial data for non-searchable lookup-selector fields on mount.
     */
    useEffect(() => {
        const lookupFields = allFields.filter(f => f.type === 'lookup-selector' && !f.lookupSearchable && f.lookupEndpoint);
        lookupFields.forEach(f => fetchLookupData(f, ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Evaluate a computed-value field expression against current form data.
     * Supports arithmetic (+, -, *, /) and string concatenation.
     */
    const computeFieldValue = (fieldDef) => {
        const expr = fieldDef.computedExpression;
        if (!expr) return '';

        // Replace {fieldName} tokens with actual values
        const resolved = expr.replace(/\{(\w+)\}/g, (_, name) => {
            const val = formData[name];
            return val !== undefined && val !== null ? String(val) : '';
        });

        // Try arithmetic evaluation if it looks like a math expression
        if (/^[\d\s+\-*/().]+$/.test(resolved.trim())) {
            try {
                // Safe arithmetic evaluation (only numbers and operators)
                const result = Function('"use strict"; return (' + resolved + ')')();
                if (typeof result === 'number' && !isNaN(result)) {
                    switch (fieldDef.computedFormat) {
                        case 'currency': return `R ${result.toFixed(2)}`;
                        case 'percentage': return `${result.toFixed(1)}%`;
                        case 'number': return result.toLocaleString();
                        default: return String(result);
                    }
                }
            } catch {
                // Fall through to string result
            }
        }
        return resolved;
    };

    /**
     * Convert value to boolean for checkbox fields
     */
    const toBoolean = (value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            const lowercased = value.toLowerCase();
            return lowercased === 'true' || lowercased === '1' || lowercased === 'yes' || lowercased === 'on';
        }
        return Boolean(value);
    };

    /**
     * Handle field blur (mark as touched and validate)
     */
    const handleBlur = (fieldName) => {
        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));

        const error = validateField(fieldName, formData[fieldName]);
        setErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
    };

    /**
     * Validate all visible fields (skips conditionally hidden fields)
     */
    const validateAll = () => {
        const newErrors = {};
        allFields.forEach(field => {
            if (!isFieldVisible(field)) return; // Skip hidden fields
            const error = validateField(field.name, formData[field.name]);
            if (error) {
                newErrors[field.name] = error;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = (e) => {
        e.preventDefault();

        if (readOnly) return;

        // Mark all visible fields as touched
        const allTouched = {};
        allFields.forEach(field => {
            if (isFieldVisible(field)) {
                allTouched[field.name] = true;
            }
        });
        setTouched(allTouched);

        // Validate all fields
        if (!validateAll()) {
            return;
        }

        // Call onSubmit with form data
        if (onSubmit) {
            onSubmit(formData);
        }
    };

    /**
     * Render a single field based on its type
     */
    const renderField = (fieldDef) => {
        const value = formData[fieldDef.name] || '';
        const error = errors[fieldDef.name];
        const isTouched = touched[fieldDef.name];

        const commonProps = {
            id: fieldDef.name,
            name: fieldDef.name,
            disabled: readOnly,
            onBlur: () => handleBlur(fieldDef.name),
            placeholder: fieldDef.placeholder || '',
            'aria-describedby': fieldDef.helpText ? `${fieldDef.name}-help` : undefined,
            'aria-invalid': error ? 'true' : 'false'
        };

        switch (fieldDef.type) {
            case 'text':
            case 'email':
                return (
                    <input
                        type={fieldDef.type}
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    />
                );

            case 'number':
                return (
                    <input
                        type="number"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        min={fieldDef.validation?.min}
                        max={fieldDef.validation?.max}
                        step={fieldDef.validation?.step || 1}
                        {...commonProps}
                    />
                );

            case 'textarea':
                return (
                    <textarea
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        rows={fieldDef.rows || 4}
                        {...commonProps}
                    />
                );

            case 'select': {
                // Dynamic options: if optionGroups is configured with dependsOnField,
                // resolve options based on the current value of the source field
                let resolvedOptions = fieldDef.options || [];
                if (fieldDef.optionGroups && fieldDef.dependsOnField) {
                    const sourceValue = formData[fieldDef.dependsOnField] || '';
                    resolvedOptions = fieldDef.optionGroups[sourceValue] || fieldDef.options || [];
                }
                return (
                    <select
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    >
                        <option value="">-- Select {fieldDef.label} --</option>
                        {resolvedOptions.map((option, idx) => (
                            <option key={idx} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );
            }

            case 'date':
                return (
                    <input
                        type="date"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    />
                );

            case 'checkbox':
                return (
                    <div className="form-check" data-field-type="checkbox" data-field-name={fieldDef.name}>
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={toBoolean(value)}
                            onChange={(e) => handleChange(fieldDef.name, e.target.checked)}
                            {...commonProps}
                        />
                        <label className="form-check-label" htmlFor={fieldDef.name}>
                            {fieldDef.label}
                        </label>
                    </div>
                );

            case 'file':
                return (
                    <input
                        type="file"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        onChange={(e) => handleChange(fieldDef.name, e.target.files[0])}
                        accept={fieldDef.accept}
                        multiple={fieldDef.multiple}
                        {...commonProps}
                    />
                );

            case 'toggle':
                return (
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={toBoolean(value)}
                                onChange={(e) => handleChange(fieldDef.name, e.target.checked)}
                                disabled={readOnly}
                                {...commonProps}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">{toBoolean(value) ? 'Yes' : 'No'}</span>
                    </div>
                );

            // Phone number field
            case 'phone':
                return (
                    <input
                        type="tel"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        pattern="[0-9+\-\s()]*"
                        {...commonProps}
                    />
                );

            // URL field
            case 'url':
                return (
                    <input
                        type="url"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    />
                );

            // Time picker
            case 'time':
                return (
                    <input
                        type="time"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    />
                );

            // DateTime picker
            case 'datetime':
                return (
                    <input
                        type="datetime-local"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    />
                );

            // Radio group
            case 'radio':
                return (
                    <div className="radio-group">
                        {(fieldDef.options || []).map((option, idx) => (
                            <div key={idx} className="form-check">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    id={`${fieldDef.name}_${idx}`}
                                    name={fieldDef.name}
                                    value={option}
                                    checked={value === option}
                                    onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                                    disabled={readOnly}
                                />
                                <label className="form-check-label" htmlFor={`${fieldDef.name}_${idx}`}>
                                    {option}
                                </label>
                            </div>
                        ))}
                    </div>
                );

            // Star rating
            case 'rating': {
                const maxRating = fieldDef.maxRating || 5;
                const currentRating = parseInt(value) || 0;
                return (
                    <div className="rating-field">
                        {[...Array(maxRating)].map((_, idx) => (
                            <button
                                key={idx}
                                type="button"
                                className={`rating-star ${idx < currentRating ? 'active' : ''}`}
                                onClick={() => !readOnly && handleChange(fieldDef.name, idx + 1)}
                                disabled={readOnly}
                                aria-label={`Rate ${idx + 1} out of ${maxRating}`}
                            >
                                <FaStar />
                            </button>
                        ))}
                        <span className="rating-value">{currentRating}/{maxRating}</span>
                    </div>
                );
            }

            // Range slider
            case 'slider': {
                const min = fieldDef.validation?.min || 0;
                const max = fieldDef.validation?.max || 100;
                const step = fieldDef.validation?.step || 1;
                return (
                    <div className="slider-field">
                        <input
                            type="range"
                            className="form-range"
                            value={value || min}
                            onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                            min={min}
                            max={max}
                            step={step}
                            disabled={readOnly}
                            {...commonProps}
                        />
                        <span className="slider-value">{value || min}</span>
                    </div>
                );
            }

            // Signature field — inserts the user's saved signature
            case 'signature': {
                const isSigned = value && value.startsWith('data:image');
                return (
                    <div className="signature-field">
                        {isSigned ? (
                            <div className="signature-pad signed">
                                <img src={value} alt="Signature" className="signature-image" />
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="signature-clear-btn"
                                        onClick={() => handleChange(fieldDef.name, '')}
                                    >
                                        <FaTimes /> Clear
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div
                                className="signature-pad"
                                onClick={() => {
                                    if (readOnly) return;
                                    if (signatureLoading) return;
                                    if (userSignature) {
                                        handleChange(fieldDef.name, userSignature);
                                    }
                                }}
                            >
                                {signatureLoading ? (
                                    <div className="signature-placeholder">
                                        <span>Loading signature...</span>
                                    </div>
                                ) : userSignature ? (
                                    <div className="signature-placeholder clickable">
                                        <FaPen />
                                        <span>Click to insert your saved signature</span>
                                    </div>
                                ) : (
                                    <div className="signature-placeholder no-signature">
                                        <FaSignature />
                                        <span>No saved signature</span>
                                        <small>Go to your Profile to draw or upload a signature first</small>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            }

            // Checklist item - compound field with description, comments, and yes/no
            case 'checklist-item': {
                const checklistValue = typeof value === 'object' ? value : { checked: false, comments: '' };
                return (
                    <div className="checklist-item-field">
                        <div className="checklist-description">
                            {fieldDef.description || fieldDef.label}
                        </div>
                        <div className="checklist-comments">
                            <textarea
                                className="form-control"
                                placeholder="Comments..."
                                value={checklistValue.comments || ''}
                                onChange={(e) => handleChange(fieldDef.name, { ...checklistValue, comments: e.target.value })}
                                disabled={readOnly}
                                rows={2}
                            />
                        </div>
                        <div className="checklist-toggle">
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={toBoolean(checklistValue.checked)}
                                    onChange={(e) => handleChange(fieldDef.name, { ...checklistValue, checked: e.target.checked })}
                                    disabled={readOnly}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className="toggle-label">{toBoolean(checklistValue.checked) ? 'Yes' : 'No'}</span>
                        </div>
                    </div>
                );
            }

            // Checklist group - container for nested items
            case 'checklist-group': {
                const groupValue = typeof value === 'object' ? value : {};
                const subItems = fieldDef.subItems || [];
                return (
                    <div className="checklist-group-field">
                        <div className="checklist-group-header">
                            {fieldDef.description || fieldDef.label}
                        </div>
                        <div className="checklist-group-items">
                            {subItems.map((subItem, idx) => {
                                const subKey = subItem.name || `sub_${idx}`;
                                const subValue = groupValue[subKey] || { checked: false, comments: '' };
                                return (
                                    <div key={idx} className="checklist-sub-item">
                                        <span className="sub-item-number">{idx + 1}.</span>
                                        <span className="sub-item-label">{subItem.label}</span>
                                        <label className="toggle-switch small">
                                            <input
                                                type="checkbox"
                                                checked={toBoolean(subValue.checked)}
                                                onChange={(e) => handleChange(fieldDef.name, { 
                                                    ...groupValue, 
                                                    [subKey]: { ...subValue, checked: e.target.checked }
                                                })}
                                                disabled={readOnly}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="checklist-group-comments">
                            <textarea
                                className="form-control"
                                placeholder="Comments for this section..."
                                value={groupValue._comments || ''}
                                onChange={(e) => handleChange(fieldDef.name, { ...groupValue, _comments: e.target.value })}
                                disabled={readOnly}
                                rows={2}
                            />
                        </div>
                    </div>
                );
            }

            // Section header (display only)
            case 'heading':
                return (
                    <div className="heading-field">
                        <h4>{fieldDef.content || fieldDef.label}</h4>
                    </div>
                );

            // Divider (display only)
            case 'divider':
                return (
                    <div className="divider-field">
                        <hr />
                    </div>
                );

            // Paragraph/Info text (display only)
            case 'paragraph':
                return (
                    <div className="paragraph-field">
                        <p>{fieldDef.content || fieldDef.description || ''}</p>
                    </div>
                );

            // Markdown Text Block – static read-only markdown rendered into HTML
            case 'markdown-block':
                return (
                    <div className="markdown-block-field">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {fieldDef.content || ''}
                        </ReactMarkdown>
                    </div>
                );

            // Spacer (display only)
            case 'spacer':
                return (
                    <div className="spacer-field" style={{ height: fieldDef.height || '20px' }}></div>
                );

            // Question / Text Block (display only) - styled callout block
            case 'question-block': {
                const qbVariants = {
                    question: { bg: '#ebf8ff', border: '#bee3f8', icon: '❓', color: '#2b6cb0' },
                    instruction: { bg: '#ebf4ff', border: '#c3dafe', icon: '📋', color: '#4338ca' },
                    warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️', color: '#b45309' },
                    note: { bg: '#f0fdf4', border: '#bbf7d0', icon: '📝', color: '#15803d' },
                    success: { bg: '#f0fdf4', border: '#86efac', icon: '✅', color: '#166534' }
                };
                const qbStyle = qbVariants[fieldDef.variant] || qbVariants.question;
                return (
                    <div className={`question-block-field variant-${fieldDef.variant || 'question'}`}
                        style={{
                            background: qbStyle.bg,
                            border: `1px solid ${qbStyle.border}`,
                            borderLeft: `4px solid ${qbStyle.color}`,
                            borderRadius: '6px',
                            padding: '12px 14px',
                            color: qbStyle.color,
                            fontSize: '0.95rem',
                            lineHeight: '1.6'
                        }}
                    >
                        {fieldDef.showIcon !== false && (
                            <span className="question-block-icon" style={{ marginRight: '8px' }}>{qbStyle.icon}</span>
                        )}
                        <span className="question-block-text">{fieldDef.content || ''}</span>
                    </div>
                );
            }

            // Icon Display (display only) - decorative FontAwesome icon element
            case 'icon-display': {
                const iconSizeMap = { small: '1rem', medium: '1.5rem', large: '2.5rem', xlarge: '4rem' };
                const renderedSize = iconSizeMap[fieldDef.iconSize] || '1.5rem';
                const renderedColor = fieldDef.iconColor || '#4a5568';
                const renderedAlign = fieldDef.iconAlign || 'left';
                return (
                    <div className="icon-display-field" style={{ textAlign: renderedAlign }}>
                        {fieldDef.iconClass ? (
                            <i className={`fas ${fieldDef.iconClass}`} style={{ fontSize: renderedSize, color: renderedColor }}></i>
                        ) : null}
                        {fieldDef.caption && (
                            <div className="icon-display-caption" style={{ fontSize: '0.85rem', color: '#718096', marginTop: '4px' }}>
                                {fieldDef.caption}
                            </div>
                        )}
                    </div>
                );
            }

            // Multi-Select — checkbox list with multiple selections
            case 'multi-select': {
                const msOpts = fieldDef.options || ['Option 1', 'Option 2', 'Option 3'];
                const msVal = Array.isArray(formData[fieldDef.name]) ? formData[fieldDef.name] : [];
                const msLayout = fieldDef.layout || 'vertical';
                return (
                    <div className={`multi-select-field layout-${msLayout}`}>
                        {msOpts.map((opt, idx) => (
                            <label key={idx} className="multi-select-option">
                                <input
                                    type="checkbox"
                                    checked={msVal.includes(opt)}
                                    onChange={(e) => {
                                        const next = e.target.checked
                                            ? [...msVal, opt]
                                            : msVal.filter(v => v !== opt);
                                        handleChange(fieldDef.name, next);
                                    }}
                                    disabled={readOnly}
                                />
                                <span>{opt}</span>
                            </label>
                        ))}
                        {fieldDef.allowOther && (
                            <label className="multi-select-option other-option">
                                <input
                                    type="checkbox"
                                    checked={msVal.includes('__other__')}
                                    onChange={(e) => {
                                        const next = e.target.checked
                                            ? [...msVal, '__other__']
                                            : msVal.filter(v => v !== '__other__');
                                        handleChange(fieldDef.name, next);
                                    }}
                                    disabled={readOnly}
                                />
                                <span>Other</span>
                                {msVal.includes('__other__') && (
                                    <input
                                        type="text"
                                        className="other-text-input"
                                        placeholder="Please specify..."
                                        value={formData[`${fieldDef.name}_other`] || ''}
                                        onChange={(e) => handleChange(`${fieldDef.name}_other`, e.target.value)}
                                        disabled={readOnly}
                                    />
                                )}
                            </label>
                        )}
                    </div>
                );
            }

            // Likert Scale — agreement/satisfaction scale
            case 'likert-scale': {
                const lkLabels = fieldDef.scaleLabels || ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
                const lkValue = formData[fieldDef.name];
                const lkShowLabels = fieldDef.showLabels !== false;
                return (
                    <div className="likert-scale-field">
                        <div className="likert-points">
                            {lkLabels.map((lbl, idx) => (
                                <div
                                    key={idx}
                                    className={`likert-point${lkValue === String(idx) ? ' selected' : ''}`}
                                    onClick={() => !readOnly && handleChange(fieldDef.name, String(idx))}
                                    role="radio"
                                    aria-checked={lkValue === String(idx)}
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!readOnly) handleChange(fieldDef.name, String(idx)); } }}
                                >
                                    <div className="likert-radio" />
                                    {lkShowLabels && <span className="likert-label">{lbl}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            }

            // Image Display (display only) — static reference image
            case 'image-display': {
                const idSrc = fieldDef.imageSrc || '';
                const idAlt = fieldDef.imageAlt || 'Image';
                const idMaxW = fieldDef.imageMaxWidth || '100%';
                const idAlign = fieldDef.imageAlign || 'left';
                return (
                    <div className="image-display-field" style={{ textAlign: idAlign }}>
                        {idSrc ? (
                            <img src={idSrc} alt={idAlt} style={{ maxWidth: idMaxW, borderRadius: '4px' }} />
                        ) : null}
                        {fieldDef.imageCaption && (
                            <div className="image-caption" style={{ fontSize: '0.85rem', color: '#718096', marginTop: '4px' }}>{fieldDef.imageCaption}</div>
                        )}
                    </div>
                );
            }

            // Info Alert (display only) — prominent banner
            case 'info-alert': {
                const iaStyles = {
                    info:    { bg: '#ebf8ff', border: '#90cdf4', icon: 'ℹ️', color: '#2b6cb0' },
                    warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️', color: '#b45309' },
                    danger:  { bg: '#fff5f5', border: '#fed7d7', icon: '🚨', color: '#c53030' },
                    success: { bg: '#f0fdf4', border: '#86efac', icon: '✅', color: '#166534' },
                    neutral: { bg: '#f7fafc', border: '#e2e8f0', icon: '📌', color: '#4a5568' }
                };
                const iaS = iaStyles[fieldDef.alertVariant] || iaStyles.info;
                return (
                    <div
                        className={`info-alert-field variant-${fieldDef.alertVariant || 'info'}`}
                        style={{
                            background: iaS.bg,
                            border: `1px solid ${iaS.border}`,
                            borderLeft: `4px solid ${iaS.color}`,
                            borderRadius: '6px',
                            padding: '12px 16px',
                            color: iaS.color,
                            fontSize: '0.95rem',
                            lineHeight: '1.6',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px'
                        }}
                    >
                        {fieldDef.showAlertIcon !== false && <span style={{ fontSize: '1.1em', flexShrink: 0 }}>{iaS.icon}</span>}
                        <span>{fieldDef.content || ''}</span>
                    </div>
                );
            }

            // Property Selector — permission-filtered dropdown
            case 'property-selector':
                return (
                    <div className="property-selector-field">
                        {dlPropertiesLoading ? (
                            <div className="data-linked-loading"><FaSpinner className="spinner" /> Loading properties…</div>
                        ) : (
                            <select
                                className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                                value={value}
                                onChange={(e) => {
                                    handleChange(fieldDef.name, e.target.value);
                                    // When property changes, clear dependent tenant/shop fields
                                    allFields.forEach(f => {
                                        if ((f.type === 'tenant-selector' || f.type === 'shop-number') && f.dependsOnField === fieldDef.name) {
                                            handleChange(f.name, '');
                                        }
                                    });
                                    // Pre-load shops for any shop-number field that depends on this selector
                                    allFields.forEach(f => {
                                        if (f.type === 'shop-number' && f.dependsOnField === fieldDef.name && e.target.value) {
                                            loadShopsForField(f.name, parseInt(e.target.value, 10));
                                        }
                                    });
                                }}
                                {...commonProps}
                            >
                                <option value="">-- Select Property --</option>
                                {dlProperties.map(p => (
                                    <option key={p.propertyID} value={p.propertyID}>
                                        {p.propertyName}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                );

            // Tenant Selector — debounced search filtered by the linked property
            case 'tenant-selector': {
                const linkedPropertyId = resolveLinkedPropertyId(fieldDef);
                const tenants = dlTenantResults[fieldDef.name] || [];
                const isSearching = dlTenantLoading[fieldDef.name] || false;
                const dropdownOpen = dlTenantDropdownOpen[fieldDef.name] || false;

                const handleTenantSelected = async (tenant) => {
                    const display = tenant.listOrTradingAsName || tenant.lesseeName || `Tenant ${tenant.tenantID}`;
                    handleChange(fieldDef.name, display);
                    // Store tenant ID for cross-referencing
                    handleChange(`${fieldDef.name}_tenantId`, String(tenant.tenantID));
                    setDlTenantDropdownOpen(prev => ({ ...prev, [fieldDef.name]: false }));

                    // Auto-populate linked fields from tenant data
                    const mappings = fieldDef.autoPopulate || {};
                    const tenantDataMap = {
                        contactName: tenant.contactName || tenant.generalContactName || '',
                        contactPhone: tenant.contactPhone || tenant.generalContactTelephoneNos || '',
                        contactEmail: tenant.contactEmail || tenant.generalContactEmailNos || '',
                        shopNumber: tenant.mainUnitNo || '',
                        tenantCode: tenant.tenantCode || '',
                        lesseeName: tenant.lesseeName || '',
                        tradingName: tenant.listOrTradingAsName || ''
                    };
                    Object.entries(mappings).forEach(([sourceKey, targetFieldName]) => {
                        if (tenantDataMap[sourceKey] !== undefined && tenantDataMap[sourceKey] !== '') {
                            handleChange(targetFieldName, tenantDataMap[sourceKey]);
                        }
                    });

                    // If shop-number field depends on same property, auto-set shop via unit-for-tenant
                    if (linkedPropertyId && tenant.tenantID) {
                        try {
                            const res = await apiFetch(`/api/AssetManagement/mda/unit-for-tenant?propertyId=${linkedPropertyId}&tenantId=${tenant.tenantID}`);
                            const data = await res.json();
                            if (data.success && data.data) {
                                // Find shop-number fields that depend on the same property selector
                                allFields.forEach(f => {
                                    if (f.type === 'shop-number' && f.dependsOnField === fieldDef.dependsOnField) {
                                        handleChange(f.name, data.data.propertyRentableUnitNo || '');
                                    }
                                });
                            }
                        } catch (err) {
                            Sentry.captureException(err);
                        }
                    }
                };

                return (
                    <div className="tenant-selector-field">
                        {!linkedPropertyId && (
                            <small className="text-muted">Select a property first.</small>
                        )}
                        <input
                            type="text"
                            className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                            value={value}
                            onChange={(e) => {
                                handleChange(fieldDef.name, e.target.value);
                                searchTenantsForField(fieldDef.name, linkedPropertyId, e.target.value);
                            }}
                            disabled={readOnly || !linkedPropertyId}
                            placeholder={linkedPropertyId ? 'Search tenant name…' : 'Select a property first'}
                            autoComplete="off"
                        />
                        {isSearching && <div className="data-linked-loading"><FaSpinner className="spinner" /> Searching…</div>}
                        {dropdownOpen && tenants.length > 0 && (
                            <div className="tenant-suggestions-dropdown">
                                {tenants.map(t => (
                                    <div
                                        key={t.tenantID}
                                        className="tenant-suggestion-item"
                                        onClick={() => handleTenantSelected(t)}
                                    >
                                        <span className="tenant-name">{t.listOrTradingAsName || t.lesseeName}</span>
                                        {t.mainUnitNo && <span className="tenant-shop">Shop {t.mainUnitNo}</span>}
                                        {t.contactName && <span className="tenant-contact">{t.contactName}</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {fieldDef.autoPopulate && !readOnly && (
                            <small className="form-text text-muted">
                                Selecting a tenant will auto-fill: {Object.values(fieldDef.autoPopulate).join(', ')}
                            </small>
                        )}
                    </div>
                );
            }

            // Shop Number — searchable dropdown filtered by the linked property.
            // Shop data is loaded via the useEffect that watches shopPropertyDependencies.
            // When a shop is selected, auto-populate linked fields via tenant-for-unit API.
            case 'shop-number': {
                const shopPropertyId = resolveLinkedPropertyId(fieldDef);
                const shops = dlShopResults[fieldDef.name] || [];
                const shopsLoading = dlShopLoading[fieldDef.name] || false;
                const shopSearchText = dlShopSearchText[fieldDef.name] || '';
                const shopDropdownVisible = dlShopDropdownOpen[fieldDef.name] || false;

                // Filter shops based on search text
                const filteredShops = shopSearchText
                    ? shops.filter(u => {
                        const unitNo = (u.propertyRentableUnitNo || u.unitNo || '').toLowerCase();
                        const desc = (u.description || '').toLowerCase();
                        const term = shopSearchText.toLowerCase();
                        return unitNo.includes(term) || desc.includes(term);
                    })
                    : shops;

                const handleShopSelected = async (unit) => {
                    const unitNo = unit.propertyRentableUnitNo || unit.unitNo || '';
                    handleChange(fieldDef.name, unitNo);
                    setDlShopSearchText(prev => ({ ...prev, [fieldDef.name]: '' }));
                    setDlShopDropdownOpen(prev => ({ ...prev, [fieldDef.name]: false }));

                    // Auto-populate from shop → tenant reverse lookup
                    if (shopPropertyId && unit.propertyRentableUnitID) {
                        try {
                            const res = await apiFetch(`/api/AssetManagement/mda/tenant-for-unit?propertyId=${shopPropertyId}&rentableUnitId=${unit.propertyRentableUnitID}`);
                            const data = await res.json();
                            if (data.success && data.data) {
                                const tenant = data.data;
                                // Auto-fill tenant-selector fields that depend on same property
                                allFields.forEach(f => {
                                    if (f.type === 'tenant-selector' && f.dependsOnField === fieldDef.dependsOnField) {
                                        const display = tenant.listOrTradingAsName || tenant.lesseeName || '';
                                        handleChange(f.name, display);
                                        handleChange(`${f.name}_tenantId`, String(tenant.tenantID));
                                        // Auto-populate the tenant-selector's linked fields too
                                        const tenantMappings = f.autoPopulate || {};
                                        const tenantDataMap = {
                                            contactName: tenant.contactName || '',
                                            contactPhone: tenant.contactPhone || '',
                                            contactEmail: tenant.contactEmail || '',
                                            shopNumber: tenant.mainUnitNo || '',
                                            tenantCode: tenant.tenantCode || '',
                                            lesseeName: tenant.lesseeName || '',
                                            tradingName: tenant.listOrTradingAsName || ''
                                        };
                                        Object.entries(tenantMappings).forEach(([sourceKey, targetFieldName]) => {
                                            if (tenantDataMap[sourceKey] !== undefined && tenantDataMap[sourceKey] !== '') {
                                                handleChange(targetFieldName, tenantDataMap[sourceKey]);
                                            }
                                        });
                                    }
                                });
                                // Also apply shop-number's own autoPopulate mappings
                                const shopMappings = fieldDef.autoPopulate || {};
                                const shopDataMap = {
                                    contactName: tenant.contactName || '',
                                    contactPhone: tenant.contactPhone || '',
                                    contactEmail: tenant.contactEmail || '',
                                    tenantName: tenant.listOrTradingAsName || tenant.lesseeName || '',
                                    tenantCode: tenant.tenantCode || '',
                                    lesseeName: tenant.lesseeName || ''
                                };
                                Object.entries(shopMappings).forEach(([sourceKey, targetFieldName]) => {
                                    if (shopDataMap[sourceKey] !== undefined && shopDataMap[sourceKey] !== '') {
                                        handleChange(targetFieldName, shopDataMap[sourceKey]);
                                    }
                                });
                            }
                        } catch (err) {
                            Sentry.captureException(err);
                        }
                    }
                };

                return (
                    <div className="shop-number-field">
                        {!shopPropertyId && (
                            <small className="text-muted">Select a property first.</small>
                        )}
                        {shopsLoading ? (
                            <div className="data-linked-loading"><FaSpinner className="spinner" /> Loading units…</div>
                        ) : (
                            <div className="shop-searchable-wrapper">
                                <input
                                    type="text"
                                    className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                                    value={value || shopSearchText}
                                    onChange={(e) => {
                                        const text = e.target.value;
                                        // If user is typing over a selected value, clear it
                                        if (value) handleChange(fieldDef.name, '');
                                        setDlShopSearchText(prev => ({ ...prev, [fieldDef.name]: text }));
                                        setDlShopDropdownOpen(prev => ({ ...prev, [fieldDef.name]: true }));
                                    }}
                                    onFocus={() => {
                                        if (shopPropertyId && !value) {
                                            setDlShopDropdownOpen(prev => ({ ...prev, [fieldDef.name]: true }));
                                        }
                                    }}
                                    disabled={readOnly || !shopPropertyId}
                                    placeholder={shopPropertyId ? 'Search shop/unit…' : 'Select a property first'}
                                    autoComplete="off"
                                />
                                {shopDropdownVisible && filteredShops.length > 0 && (
                                    <div className="tenant-suggestions-dropdown">
                                        {filteredShops.map(u => (
                                            <div
                                                key={u.propertyRentableUnitID || u.id}
                                                className="tenant-suggestion-item"
                                                onClick={() => handleShopSelected(u)}
                                            >
                                                <span className="tenant-name">{u.propertyRentableUnitNo || u.unitNo}</span>
                                                {u.description && <span className="tenant-shop">{u.description}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {fieldDef.autoPopulate && !readOnly && (
                            <small className="form-text text-muted">
                                Selecting a shop will auto-fill: {Object.values(fieldDef.autoPopulate).join(', ')}
                            </small>
                        )}
                    </div>
                );
            }

            // Entra ID User Selector — searchable 365 user lookup with auto-populate
            case 'entra-user-selector': {
                const entraResults = entraSearchResults[fieldDef.name] || [];
                const isSearching = entraSearchLoading[fieldDef.name] || false;
                const dropdownOpen = entraDropdownOpen[fieldDef.name] || false;

                return (
                    <div className="entra-user-selector-field">
                        <input
                            type="text"
                            className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                            value={value}
                            onChange={(e) => {
                                handleChange(fieldDef.name, e.target.value);
                                searchEntraUsersForField(fieldDef.name, e.target.value);
                            }}
                            disabled={readOnly}
                            placeholder={fieldDef.placeholder || 'Search by name or email...'}
                            autoComplete="off"
                        />
                        {isSearching && <div className="data-linked-loading"><FaSpinner className="spinner" /> Searching Microsoft 365…</div>}
                        {dropdownOpen && entraResults.length > 0 && (
                            <div className="entra-suggestions-dropdown">
                                {entraResults.map(u => (
                                    <div
                                        key={u.id}
                                        className="entra-suggestion-item"
                                        onClick={() => handleEntraUserSelected(fieldDef, u)}
                                    >
                                        <div className="entra-suggestion-name">{u.displayName}</div>
                                        <div className="entra-suggestion-details">
                                            {u.jobTitle && <span className="entra-detail">{u.jobTitle}</span>}
                                            {u.department && <span className="entra-detail">{u.department}</span>}
                                            {u.email && <span className="entra-detail entra-email">{u.email}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {fieldDef.autoPopulate && !readOnly && (
                            <small className="form-text text-muted">
                                Selecting a user will auto-fill: {Object.keys(fieldDef.autoPopulate).join(', ')}
                            </small>
                        )}
                    </div>
                );
            }

            // Role Selector — CapTrac application role dropdown
            case 'role-selector':
                return (
                    <div className="role-selector-field">
                        {capTracRolesLoading ? (
                            <div className="data-linked-loading"><FaSpinner className="spinner" /> Loading roles…</div>
                        ) : (
                            <select
                                className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                                value={value}
                                onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                                disabled={readOnly}
                                {...commonProps}
                            >
                                <option value="">-- Select Role --</option>
                                {capTracRoles.map(r => (
                                    <option key={r.roleID} value={r.roleName}>
                                        {r.roleName}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                );

            // Lookup Selector — generic API-backed dropdown
            case 'lookup-selector': {
                const lookupData = lookupResults[fieldDef.name] || [];
                const lookupLoading = lookupLoadingMap[fieldDef.name] || false;

                if (fieldDef.lookupSearchable) {
                    return (
                        <div className="lookup-selector-field">
                            <input
                                type="text"
                                className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                                value={value}
                                onChange={(e) => {
                                    handleChange(fieldDef.name, e.target.value);
                                    fetchLookupData(fieldDef, e.target.value);
                                }}
                                disabled={readOnly}
                                placeholder={fieldDef.placeholder || 'Type to search...'}
                                autoComplete="off"
                            />
                            {lookupLoading && <div className="data-linked-loading"><FaSpinner className="spinner" /> Searching…</div>}
                            {lookupData.length > 0 && (
                                <div className="lookup-suggestions-dropdown">
                                    {lookupData.map((item, idx) => (
                                        <div
                                            key={item[fieldDef.lookupValueField] || idx}
                                            className="lookup-suggestion-item"
                                            onClick={() => handleChange(fieldDef.name, String(item[fieldDef.lookupValueField] || item[fieldDef.lookupDisplayField] || ''))}
                                        >
                                            {item[fieldDef.lookupDisplayField] || JSON.stringify(item)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }

                return (
                    <div className="lookup-selector-field">
                        {lookupLoading ? (
                            <div className="data-linked-loading"><FaSpinner className="spinner" /> Loading…</div>
                        ) : (
                            <select
                                className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                                value={value}
                                onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                                disabled={readOnly}
                                {...commonProps}
                            >
                                <option value="">-- Select --</option>
                                {lookupData.map((item, idx) => (
                                    <option key={item[fieldDef.lookupValueField] || idx} value={item[fieldDef.lookupValueField] || ''}>
                                        {item[fieldDef.lookupDisplayField] || JSON.stringify(item)}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                );
            }

            // Repeater — dynamic add/remove rows of sub-fields
            case 'repeater': {
                const rows = Array.isArray(value) ? value : [];
                const subFields = fieldDef.repeaterFields || [];
                const minRows = fieldDef.repeaterMinRows ?? 1;
                const maxRows = fieldDef.repeaterMaxRows ?? 0;

                const handleRepeaterRowChange = (rowIdx, subFieldName, newVal) => {
                    const updatedRows = rows.map((row, i) =>
                        i === rowIdx ? { ...row, [subFieldName]: newVal } : row
                    );
                    handleChange(fieldDef.name, updatedRows);
                };

                const addRow = () => {
                    if (maxRows > 0 && rows.length >= maxRows) return;
                    const emptyRow = {};
                    subFields.forEach(sf => { emptyRow[sf.name] = ''; });
                    handleChange(fieldDef.name, [...rows, emptyRow]);
                };

                const removeRow = (rowIdx) => {
                    if (rows.length <= minRows) return;
                    handleChange(fieldDef.name, rows.filter((_, i) => i !== rowIdx));
                };

                return (
                    <div className="repeater-field">
                        {subFields.length > 0 && (
                            <div className="repeater-header">
                                {subFields.map(sf => (
                                    <span key={sf.name} className="repeater-col-header">{sf.label}</span>
                                ))}
                                <span className="repeater-col-header repeater-actions-header"></span>
                            </div>
                        )}
                        {rows.map((row, rowIdx) => (
                            <div key={rowIdx} className="repeater-row">
                                {subFields.map(sf => (
                                    <div key={sf.name} className="repeater-cell">
                                        <input
                                            type={sf.type === 'number' ? 'number' : 'text'}
                                            className="form-control form-control-sm"
                                            value={row[sf.name] || ''}
                                            onChange={(e) => handleRepeaterRowChange(rowIdx, sf.name, e.target.value)}
                                            disabled={readOnly}
                                            placeholder={sf.label}
                                        />
                                    </div>
                                ))}
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger repeater-remove-btn"
                                        onClick={() => removeRow(rowIdx)}
                                        disabled={rows.length <= minRows}
                                        title="Remove row"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        ))}
                        {!readOnly && (maxRows === 0 || rows.length < maxRows) && (
                            <button type="button" className="btn btn-sm btn-outline-secondary repeater-add-btn" onClick={addRow}>
                                + Add Row
                            </button>
                        )}
                    </div>
                );
            }

            // Computed Value — read-only calculated field
            case 'computed-value': {
                const computedResult = computeFieldValue(fieldDef);
                return (
                    <div className="computed-value-field">
                        <input
                            type="text"
                            className="form-control"
                            value={computedResult}
                            readOnly
                            disabled
                        />
                    </div>
                );
            }

            default:
                return (
                    <input
                        type="text"
                        className={`form-control ${error && isTouched ? 'is-invalid' : ''}`}
                        value={value}
                        onChange={(e) => handleChange(fieldDef.name, e.target.value)}
                        {...commonProps}
                    />
                );
        }
    };

    /**
     * Render a field with label, input, help text, and error message
     */
    const renderFieldGroup = (fieldDef) => {
        // Conditional visibility: skip rendering if conditions are not met
        if (!isFieldVisible(fieldDef)) return null;

        const error = errors[fieldDef.name];
        const isTouched = touched[fieldDef.name];
        const fieldType = (fieldDef.type || '').toLowerCase().trim();

        // Layout-only fields (no label wrapper needed)
        const layoutOnlyTypes = ['heading', 'divider', 'paragraph', 'spacer', 'markdown-block', 'question-block', 'icon-display', 'image-display', 'info-alert'];
        if (layoutOnlyTypes.includes(fieldType)) {
            return (
                <div key={fieldDef.name} className={`form-group field-group ${fieldDef.type}-wrapper full-width`} data-field-type={fieldType}>
                    {renderField(fieldDef)}
                </div>
            );
        }

        // Checklist fields have special layout
        if (fieldType === 'checklist-item' || fieldType === 'checklist-group') {
            return (
                <div key={fieldDef.name} className={`form-group field-group ${fieldDef.type}-wrapper full-width`} data-field-type={fieldType}>
                    {renderField(fieldDef)}
                    {fieldDef.helpText && (
                        <small id={`${fieldDef.name}-help`} className="form-text text-muted">
                            {fieldDef.helpText}
                        </small>
                    )}
                    {error && isTouched && (
                        <div className="invalid-feedback d-block">{error}</div>
                    )}
                </div>
            );
        }

        // Checkbox and toggle types have different layout (no separate label needed)
        if (fieldType === 'checkbox' || fieldType === 'toggle') {
            return (
                <div key={fieldDef.name} className="form-group field-group checkbox-field" data-field-type={fieldType} data-field-name={fieldDef.name}>
                    <div className="checkbox-wrapper">
                        {renderField(fieldDef)}
                        {fieldDef.helpText && (
                            <small id={`${fieldDef.name}-help`} className="form-text">
                                {fieldDef.helpText}
                            </small>
                        )}
                    </div>
                    {error && isTouched && (
                        <div className="invalid-feedback d-block">{error}</div>
                    )}
                </div>
            );
        }

        // Radio group
        if (fieldType === 'radio') {
            return (
                <div key={fieldDef.name} className="form-group field-group radio-field" data-field-type={fieldType}>
                    <label className="form-label">
                        {fieldDef.label}
                        {fieldDef.required && <span className="required-indicator">*</span>}
                    </label>
                    {renderField(fieldDef)}
                    {fieldDef.helpText && (
                        <small id={`${fieldDef.name}-help`} className="form-text text-muted">
                            {fieldDef.helpText}
                        </small>
                    )}
                    {error && isTouched && (
                        <div className="invalid-feedback d-block">{error}</div>
                    )}
                </div>
            );
        }

        // Default rendering for other field types
        return (
            <div key={fieldDef.name} className="form-group field-group" data-field-type={fieldType}>
                <label htmlFor={fieldDef.name} className="form-label">
                    {fieldDef.label}
                    {fieldDef.required && <span className="required-indicator">*</span>}
                </label>
                {renderField(fieldDef)}
                {fieldDef.helpText && (
                    <small id={`${fieldDef.name}-help`} className="form-text text-muted">
                        {fieldDef.helpText}
                    </small>
                )}
                {error && isTouched && (
                    <div className="invalid-feedback d-block">{error}</div>
                )}
            </div>
        );
    };

    /**
     * Render a field or row container with its nested fields.
     * Handles both regular fields and row-type fields with Bootstrap-style columns.
     * @param {object} field - The field definition
     * @param {number} fieldIdx - The field index for key generation
     */
    const renderFieldOrRow = (field, fieldIdx) => {
        if (field.type === 'row') {
            const columns = field.columns || [];
            
            // Don't show auto-generated or placeholder row labels
            const label = field.label?.trim() || '';
            const isAutoLabel = !label || 
                                label === 'New Row' || 
                                label.toLowerCase().startsWith('row') ||
                                /^section\s*\d*\s*row\s*\d*$/i.test(label);
            
            // Calculate total span for proper flex distribution
            const totalSpan = columns.reduce((sum, col) => sum + (col.span || 6), 0) || 12;
            
            return (
                <div 
                    key={field.id || field.name || fieldIdx} 
                    className="field-row-container"
                >
                    {!isAutoLabel && (
                        <label className="field-row-label">{label}</label>
                    )}
                    <div className="field-row-columns">
                        {columns.map((column, colIdx) => {
                            const colSpan = column.span || 6;
                            const flexBasis = (colSpan / totalSpan) * 100;
                            
                            return (
                                <div 
                                    key={column.id || colIdx} 
                                    className="row-column-wrapper"
                                    style={{ 
                                        // Use proportional flex-basis only; rely on CSS `gap` for spacing to avoid
                                        // hardcoded coupling between JavaScript and SCSS gap values.
                                        flex: `1 1 ${flexBasis}%`,
                                        maxWidth: `${flexBasis}%`
                                    }}
                                >
                                    {column.fields?.map((colField, colFieldIdx) => (
                                        <React.Fragment key={colField.id || colField.name || colFieldIdx}>
                                            {renderFieldGroup(colField)}
                                        </React.Fragment>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return (
            <React.Fragment key={field.name || field.id || fieldIdx}>
                {renderFieldGroup(field)}
            </React.Fragment>
        );
    };

    return (
        <div className="template-renderer">
            <form onSubmit={handleSubmit} noValidate>
                {/* Support new sections-based structure */}
                {templateSections.length > 0 ? (
                    templateSections.map((section, sectionIdx) => (
                        <div key={section.id || sectionIdx} className="template-section">
                            {section.title && (
                                <h3 className="section-title">{section.title}</h3>
                            )}
                            <div 
                                className="section-fields" 
                                style={section.columns > 1 ? {
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${section.columns}, 1fr)`,
                                    gap: '1rem'
                                } : {}}
                            >
                                {section.fields?.map((field, fieldIdx) => renderFieldOrRow(field, fieldIdx))}
                            </div>
                        </div>
                    ))
                ) : (
                    /* Support old flat fields with layout structure */
                    <div className="template-section">
                        <div className="section-fields">
                            {fields.map((fieldDef, fieldIdx) => renderFieldOrRow(fieldDef, fieldIdx))}
                        </div>
                    </div>
                )}

                {!readOnly && onSubmit && (
                    <div className="form-actions">
                        <button type="submit" className="btn btn-primary">
                            <FaCheck /> {submitButtonText}
                        </button>
                        {onCancel && (
                            <button type="button" className="btn btn-secondary" onClick={onCancel}>
                                <FaTimes /> Cancel
                            </button>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
};

TemplateRenderer.propTypes = {
    template: PropTypes.shape({
        schema: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        displayName: PropTypes.string,
        description: PropTypes.string
    }).isRequired,
    initialData: PropTypes.object,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    onChange: PropTypes.func,
    submitButtonText: PropTypes.string,
    readOnly: PropTypes.bool,
    externalPropertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default TemplateRenderer;
